import { Alert_, AlertContext, Grey_Link, Main_Loader, PageAlwaysVisible, PageSixty, PageTitle, Section_Title, Single_, ThemeContext, logo } from 'monica-alexandria';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../localization/i18next';
import store from '../../redux/store';
import currencies from '../../data/currencies.json';
import { get_my_subscriptions, create_portal_session } from '../../services/api/Mocks';
import { create_artist_portal_session } from '../../services/api/Subscriptions';
import { get_product_invoices } from '../../services/api/Transactions';
import { prettifyDates } from '../../helpers/prettifiers';
import { prettifyPrice } from '../../helpers/prettifyFunctions';
import Subscription_Plan from '../subscription/Subscription_Plan';
import Subscription_History_Block from '../subscription/Subscription_History_Block';

export default function Product_Subscription(props) {
  const userId = store.getState()?.user?.user?.sub;
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const { alerts, setAlerts } = useContext(AlertContext);
  const navigate = useNavigate();
  const lang = i18n.language || 'en';
  const [plan, setPlan] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [productInvoices, setProductInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const redirectUrl = `${window.location.origin}${window.location.pathname}`;

  const open_portal = () => {
    setPortalLoading(true);

    if (props.category === 'artist') {
      create_artist_portal_session(redirectUrl)
        .then(data => {
          if (data?.url) window.location.assign(data.url);
          else {
            setAlerts([...alerts, { message: { text: t('Could not open billing management. Please try again.'), status: 'Error' } }]);
            setPortalLoading(false);
          }
        });
    } else {
      create_portal_session({customer: plan?.customer, lang, userId, redirectURL: redirectUrl})
        .then(data => {
          if (data?.data?.url) window.location.assign(data.data.url);
          else {
            setAlerts([...alerts, { message: { text: t('Could not open billing management. Please try again.'), status: 'Error' } }]);
            setPortalLoading(false);
          }
        });
    }
  };

  const open_invoice = invoice => {
    const productInvoice = productInvoices.find(item => item?.invoiceId === invoice?.id);

    if (productInvoice?.wrappId) {
      window.open(`https://wrapp.ai/${lang}/customer_portal/${productInvoice.wrappId}`, '_blank', 'noopener,noreferrer');
    } else if (invoice?.invoice_pdf) {
      window.open(invoice.invoice_pdf, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    get_my_subscriptions(userId)
      .then(data => {
        const subscription = data?.data?.find(item => item?.category === props.category);
        const activePlan = subscription?.tools?.find(item => item?.active);
        setPlan(activePlan || subscription?.tools?.[subscription.tools.length - 1] || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [props.category, userId]);

  useEffect(() => {
    if (!plan?.customer || !plan?.subscription) return;

    get_product_invoices(plan.customer, props.category, plan.subscription)
      .then(data => {
        setTransactions(data?.data || []);
        setProductInvoices(data?.productInvoices || []);
      });
  }, [plan, props.category]);

  return (
    <PageSixty>
      <Alert_></Alert_>
      <PageAlwaysVisible>
        <PageTitle>
          <Grey_Link noPadding iconLeft="Back" onClick={() => navigate(-1)}></Grey_Link>
          <h1>{t(props.name)} {t('subscription')}</h1>
        </PageTitle>
        {loading ? (
          <Main_Loader></Main_Loader>
        ) : plan ? (
          <Subscription_Plan
            category={props.category}
            currentPlan={t(props.name)}
            img={logo?.[props.category]?.[theme] || logo.griphook?.[theme]}
            onEditCard={open_portal}
            currency={currencies[plan.currency?.toUpperCase()]?.symbol || plan.currency?.toUpperCase()}
            price={prettifyPrice(plan.amount)}
            isActive={plan.active}
            taxBehavior={plan.taxBehavior}
            interval={plan.interval}
            interval_count={plan.interval_count}
            cancel_at_period_end={plan.cancel_at_period_end}
            trial={plan.status === 'trialing'}
            failed={plan.status === 'past_due'}
            daysLeft={Math.ceil((plan.current_period_end * 1000 - Date.now()) / (1000 * 60 * 60 * 24))}
            nextPayment={!plan.active || plan.cancel_at_period_end ? t('Auto renewal canceled') : prettifyDates(plan.current_period_end, true, lang)}
            cardNo={transactions[transactions.length - 1]?.customer?.invoice_settings?.default_payment_method?.card?.last4 || 'no card'}
            loading={portalLoading}
          ></Subscription_Plan>
        ) : (
          <Single_ title={t('Unavailable')} subtitle={t("We're sorry, right now you can't access these information. Try refreshing the page.")}></Single_>
        )}
      </PageAlwaysVisible>
      {!loading && plan ? (
        <PageAlwaysVisible>
          <Section_Title text={t('Payment History')}></Section_Title>
          {transactions.map(invoice => (
            <Subscription_History_Block
              key={invoice.id}
              currency={currencies[invoice.currency?.toUpperCase()]?.symbol || invoice.currency?.toUpperCase()}
              price={prettifyPrice(invoice.total)}
              status={invoice.status}
              date={prettifyDates(invoice.created, true, lang)}
              invoice={Boolean(productInvoices.find(item => item?.invoiceId === invoice?.id)?.wrappId || invoice.invoice_pdf) && Number(invoice.total) > 0}
              onInvoice={() => open_invoice(invoice)}
            ></Subscription_History_Block>
          ))}
        </PageAlwaysVisible>
      ) : null}
    </PageSixty>
  );
}
